import React, { useState, useMemo } from "react";
import { Confetti, playCorrect, playWrong } from "@/components/Bits";
import { Cell, DoneCard, RoundHeader } from "@/components/Cells";
import { shuffle } from "@/lib/quizgen";
import { recordAnswer, completeActivity } from "@/lib/progress";
import { speak } from "@/lib/speech";

/* Two-column pair matching. rounds: [[{id,left,right}, ...], ...] */
export default function MatchRunner({ title, category, modeId, build, backTo = "/learn" }) {
  const [seed, setSeed] = useState(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rounds = useMemo(() => build(seed).map(pairs => ({ pairs, left: shuffle(pairs), right: shuffle(pairs) })), [seed, build]);
  const [r, setR] = useState(0);
  const [selL, setSelL] = useState(null);
  const [matched, setMatched] = useState([]);
  const [shake, setShake] = useState(null);
  const [done, setDone] = useState(false);
  const round = rounds[r];

  const tapRight = (id) => {
    if (!selL || matched.includes(id)) return;
    const ok = id === selL;
    recordAnswer(category, ok);
    if (!ok) { playWrong(); setShake(id); setTimeout(() => setShake(null), 450); setSelL(null); return; }
    playCorrect();
    const nm = [...matched, id]; setMatched(nm); setSelL(null);
    if (nm.length === round.pairs.length) {
      setTimeout(() => {
        if (r + 1 >= rounds.length) { completeActivity(category, modeId); setDone(true); speak("All matched! Wonderful!"); }
        else { setR(r + 1); setMatched([]); speak("Next round!"); }
      }, 700);
    }
  };
  const again = () => { setSeed(s => s + 1); setR(0); setMatched([]); setSelL(null); setDone(false); };

  if (done) return <DoneCard title="All matched!" emoji="🧩" onAgain={again} backTo={backTo} subtitle={`You finished ${title}!`} />;

  const tile = (active, isMatched, extra = "") => `ll-tile bg-white flex items-center justify-center p-3 min-h-[80px] sm:min-h-[100px] ${active ? "ring-4 ring-purple-400 scale-105" : ""} ${isMatched ? "opacity-40 ring-4 ring-emerald-300" : ""} ${extra}`;

  return (
    <div className="ll-bg min-h-screen pb-32">
      <div className="max-w-3xl mx-auto px-4 pt-4">
        <RoundHeader idx={r} total={rounds.length} label={`Round ${r + 1} / ${rounds.length}`} />
        <div className="ll-card p-5 sm:p-8">
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-purple-900">{title}</h2>
          <p className="text-purple-600 font-semibold mt-1" data-testid="match-hint">{selL ? "Now tap its match on the right!" : "Tap one on the left, then its match on the right."}</p>
          <div className="grid grid-cols-2 gap-6 sm:gap-10 mt-5">
            <div className="grid gap-3">
              {round.left.map(p => (
                <button key={p.id} data-testid={`match-left-${p.id}`} disabled={matched.includes(p.id)} onClick={() => setSelL(p.id)} className={tile(selL === p.id, matched.includes(p.id))}>
                  <Cell c={p.left} size="sm" />
                </button>
              ))}
            </div>
            <div className="grid gap-3">
              {round.right.map(p => (
                <button key={p.id} data-testid={`match-right-${p.id}`} disabled={matched.includes(p.id)} onClick={() => tapRight(p.id)} className={tile(false, matched.includes(p.id), shake === p.id ? "ll-shake ring-4 ring-pink-300" : "")}>
                  <Cell c={p.right} size="sm" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Confetti show={matched.length === round.pairs.length} />
    </div>
  );
}
